import type { CallInfo, Chat, Pinned, Scheduled } from "@rhema/client";
import { previewOf } from "@rhema/client";
import {
  Avatar,
  Button,
  ButtonLink,
  ClockIcon,
  cn,
  focusRing,
  HeadphonesIcon,
  PinIcon,
  Sheet,
} from "@rhema/ui";
import { useState } from "react";
import { toneFor } from "../data/view";
import { TimeSheet } from "../later/TimeSheet";
import { sendChoices, whenLabel } from "../later/when";
import { nameIn, textOf } from "./MessageList";
import { MessageText, useRichRender } from "./rich";

const bar =
  "flex flex-none items-center gap-3 border-b border-line bg-linen px-4 py-2 text-left min-[900px]:px-6";

/** The newest pinned message under the header. Opens the full list. */
export function PinnedBar({ pins, onOpen }: { pins: Pinned[]; onOpen: () => void }) {
  const first = pins[0];
  if (!first) return null;
  return (
    <button type="button" onClick={onOpen} className={cn(bar, "w-full cursor-pointer", focusRing)}>
      <PinIcon size={16} strokeWidth={2.2} className="flex-none text-blue" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-[12.5px] font-bold text-blue">
          {pins.length === 1 ? "Pinned message" : `${pins.length} pinned messages`}
        </span>
        <span className="truncate text-[13.5px] text-ink-secondary">{textOf(first.message)}</span>
      </span>
    </button>
  );
}

export function PinsSheet({
  open,
  chat,
  pins,
  meId,
  canPin,
  onUnpin,
  onClose,
}: {
  open: boolean;
  chat: Chat;
  pins: Pinned[];
  meId: string | undefined;
  canPin: boolean;
  onUnpin: (messageId: string) => void;
  onClose: () => void;
}) {
  const render = useRichRender(chat);
  return (
    <Sheet open={open} onClose={onClose} title="Pinned messages" fixed={false} className="pb-7">
      {pins.length === 0 ? (
        <p className="m-0 mt-3 text-[15px] text-ink-secondary">Nothing is pinned in this chat.</p>
      ) : (
        <ul className="m-0 mt-3 flex list-none flex-col gap-2 p-0">
          {pins.map((p) => (
            <li key={p.message.id} className="rounded-card border border-line bg-surface px-3.5 py-3">
              <div className="flex items-center gap-2.5">
                <Avatar
                  name={nameIn(chat, p.message.senderId, meId)}
                  tone={toneFor(p.message.senderId ?? "")}
                  size={28}
                />
                <span className="min-w-0 flex-1 truncate text-sm font-bold">
                  {nameIn(chat, p.message.senderId, meId)}
                </span>
                {canPin && (
                  <Button variant="ghost" size="sm" onClick={() => onUnpin(p.message.id)}>
                    Unpin
                  </Button>
                )}
              </div>
              <div className="m-0 mt-2 text-[15px] leading-normal whitespace-pre-wrap">
                {p.message.content?.type === "text" ? (
                  <MessageText m={p.message} chat={chat} render={render} />
                ) : (
                  textOf(p.message)
                )}
              </div>
              <p className="m-0 mt-1.5 text-xs text-ink-meta">
                Pinned by {nameIn(chat, p.pinnedBy, meId)} {whenLabel(p.pinnedAt).toLowerCase()}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
}

/** Your scheduled messages in this chat, above the composer. */
export function ScheduledBar({ items, onOpen }: { items: Scheduled[]; onOpen: () => void }) {
  if (!items.length) return null;
  const next = items[0]!;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "mx-3 mb-1 flex flex-none cursor-pointer items-center gap-2.5 rounded-field bg-surface-header px-3.5 py-2 text-left min-[900px]:mx-5",
        focusRing,
      )}
    >
      <ClockIcon size={16} strokeWidth={2.2} className="flex-none text-blue" />
      <span className="min-w-0 flex-1 truncate text-[13.5px]">
        {items.length === 1
          ? `1 message scheduled for ${whenLabel(next.sendAt).toLowerCase()}`
          : `${items.length} messages scheduled. The next goes ${whenLabel(next.sendAt).toLowerCase()}`}
      </span>
      <span className="text-[13px] font-semibold text-blue">View</span>
    </button>
  );
}

export function ScheduledSheet({
  open,
  items,
  onSendNow,
  onCancel,
  onReschedule,
  onClose,
}: {
  open: boolean;
  items: Scheduled[];
  onSendNow: (id: string) => void;
  onCancel: (id: string) => void;
  onReschedule: (id: string, at: Date) => void;
  onClose: () => void;
}) {
  const [moving, setMoving] = useState<string | null>(null);
  return (
    <>
      <Sheet
        open={open && !moving}
        onClose={onClose}
        title="Scheduled messages"
        subtitle="They are encrypted on this device now and sent at the time shown."
        fixed={false}
        className="pb-7"
      >
        {items.length === 0 ? (
          <p className="m-0 mt-3 text-[15px] text-ink-secondary">Nothing is scheduled in this chat.</p>
        ) : (
          <ul className="m-0 mt-3 flex list-none flex-col gap-2 p-0">
            {items.map((s) => (
              <li key={s.id} className="rounded-card border border-line bg-surface px-3.5 py-3">
                <p className="m-0 text-[13px] font-semibold text-blue">{whenLabel(s.sendAt)}</p>
                <p className="m-0 mt-1 text-[15px] leading-normal whitespace-pre-wrap">
                  {s.content ? previewOf(s.content) : "Scheduled from another device"}
                </p>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => onSendNow(s.id)}>
                    Send now
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setMoving(s.id)}>
                    Change time
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => onCancel(s.id)}>
                    Delete
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Sheet>
      <TimeSheet
        open={moving !== null}
        title="Change the time"
        choices={sendChoices()}
        pickLabel="Save the time"
        onPick={(at) => {
          if (moving) onReschedule(moving, at);
          setMoving(null);
        }}
        onClose={() => setMoving(null)}
      />
    </>
  );
}

/** A huddle going on in a channel, with who is in it and a way in. */
export function HuddleBar({
  chat,
  huddle,
  meId,
  inIt,
}: {
  chat: Chat;
  huddle: CallInfo;
  meId: string | undefined;
  inIt: boolean;
}) {
  const people = huddle.participants.filter((p) => p.state === "joined");
  const names = people.map((p) => nameIn(chat, p.user_id, meId));
  return (
    <div className={cn(bar, "bg-blue-tint")} role="status">
      <HeadphonesIcon size={18} strokeWidth={2.2} className="flex-none text-blue-dark" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-[13.5px] font-bold text-blue-dark">Huddle in #{chat.title}</span>
        <span className="truncate text-[13px] text-ink-secondary">
          {names.length ? names.join(", ") : "Nobody is in it yet"}
        </span>
      </span>
      {inIt ? (
        <ButtonLink href={`/calls/${chat.id}`} size="sm" variant="tonal">
          Open
        </ButtonLink>
      ) : (
        <ButtonLink href={`/calls/${chat.id}?media=voice`} size="sm">
          Join
        </ButtonLink>
      )}
    </div>
  );
}
